import { Document, Page, Text, View, Image, StyleSheet } from '@react-pdf/renderer';
import logoPdf from '../../assets/logoPdf.jpg';

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontFamily: 'Helvetica',
    fontSize: 12,
    display: 'flex',
    flexDirection: 'column',
    gap: 30
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  logo: { width: 120, height: 40 },
  logoEmpresa: { width: 110, height: 35 },
  tituloPrincipal: {
    textAlign: 'center',
    fontSize: 20,
    fontWeight: 'bold',
    marginTop: 10,
  },
  content: { flexDirection: 'column', gap: 20, width: '100%' },
  perfilBox: {
    flexDirection: 'row',
    gap: 5,
    alignItems: 'center',
    fontSize: 16,
    fontWeight: 'bold',
    marginTop: -10,
    marginBottom: 4,
  },
  tabela: {
    width: '100%',
    backgroundColor: '#f9f9f9',
    padding: 12,
    borderRadius: 8,
    border: '1px solid #ccc',
  },
  tableHeader: {
    backgroundColor: '#e0e0e0',
    padding: 6,
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderRadius: 4,
    marginBottom: 4,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 5,
    borderBottom: '1px solid #ddd',
  },
  lastRow: { borderBottom: 'none' },
  leftText: { color: '#333' },
  rightText: { fontWeight: 'bold', color: '#333' },
  grafico: {
    width: '100%',
    height: 400,
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    border: '1px solid #ccc',
    borderRadius: 8,
    padding: 10,
  },
  graficoImagem: { width: '100%', height: '100%', objectFit: 'contain' }
});

/**
 * props:
 * - predominantLabel: string  (ex.: "A + C" ou "FOGO")
 * - scores: Array<{ profile: string, percent: number|string, color?: string }>
 * - graficoBase64: string
 * - empresa, turma, empresaLogoBase64
 */
export default function PDFResultado({
  predominantLabel,
  scores = [],
  graficoBase64,
  empresa,
  turma,
  empresaLogoBase64
}) {
  // Cor do título do perfil predominante: pega a cor do primeiro perfil que aparecer em predominantLabel
  const predominantColor = (() => {
    if (!predominantLabel) return '#00AFA9';
    const parts = predominantLabel.split('+').map(p => p.trim().toUpperCase());
    for (const p of parts) {
      const hit = scores.find(s => String(s.profile).toUpperCase() === p);
      if (hit?.color) return hit.color;
    }
    // fallback: tenta a cor do top-1 dos scores
    return scores[0]?.color || '#00AFA9';
  })();

  // Linhas da tabela: dinâmico pelos scores
  const linhas = scores.map((s, idx) => ({
    rotulo: `Grupo ${String(idx + 1).padStart(2, '0')} - ${s.profile}`,
    valor: `${Number(s.percent || 0)}%`,
    cor: s.color || '#00AFA9'
  }));

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Image src={logoPdf} style={styles.logo} />
          {empresaLogoBase64 ? <Image src={empresaLogoBase64} style={styles.logoEmpresa} /> : <View />}
        </View>

        <Text style={styles.tituloPrincipal}>
          {(empresa?.nome || 'Empresa') + ' - ' + (turma?.nome || 'Turma')}
        </Text>

        <View style={styles.content}>
          {/* Perfil predominante */}
          <View style={styles.perfilBox}>
            <Text>Perfil predominante:</Text>
            <Text style={{ color: predominantColor }}>
              {predominantLabel}
            </Text>
          </View>

          {/* Tabela dinâmica */}
          <View style={styles.tabela}>
            <View style={styles.tableHeader}>
              <Text style={{ fontWeight: 'bold' }}>Resultado</Text>
              <Text style={{ fontWeight: 'bold' }}>Percentual</Text>
            </View>

            {linhas.map((item, index) => (
              <View
                key={index}
                style={[styles.row, index === linhas.length - 1 && styles.lastRow]}
              >
                <Text style={[styles.leftText, { color: item.cor }]}>
                  {item.rotulo}
                </Text>
                <Text style={[styles.rightText, { color: item.cor }]}>
                  {item.valor}
                </Text>
              </View>
            ))}
          </View>

          {/* Gráfico (imagem gerada off-screen) */}
          <View style={styles.grafico}>
            {graficoBase64 ? <Image src={graficoBase64} style={styles.graficoImagem} /> : <Text>Gráfico indisponível</Text>}
          </View>
        </View>
      </Page>
    </Document>
  );
}